/**
 * Per-mode behavior graphs for bot turn planning.
 * Pure functions — no Three.js, no side effects, deterministic given same inputs.
 *
 * Each game mode defines a planner that maps a BotSituation snapshot to an
 * ordered list of BotPlanSteps. The steps execute sequentially within one turn.
 * Deathmatch goes through here too: its flat action scoring (bot/decision.ts)
 * picks the action, and deathmatchPlanFor turns it into the same step list.
 */

import { arcDistance } from '../core/gameState';
import { MOUNT_MOVEMENT_COST, INTERACT_RANGE } from '../constants';
import { tuningFor } from './bot/tuning';
import type {
  PlayerState,
  GameState,
  BotConfig,
  BotPersonality,
  BotFogContext,
  BotSituation,
  BotTurnSteps,
  BotPlanStep,
  BotCommand,
  TransitGraph,
  GameMode,
} from '../types';
import {
  FIRE_MOVEMENT_COST,
  JUMP_MIN_COST,
  JUMPPACK_VELOCITY_BONUS,
} from '../constants';
import { hasAnyAmmo, chooseBotAction, type BotAction } from './bot';
import { alertLevelAt, isGuard } from '../core/perception';
import { sneakingNow } from './bot/sneak';
import {
  getBotObjectiveTarget,
  getBotSamePlanetObjectiveDir,
  canBotPlantStake,
  canBotRemoveStake,
  canBotInteract,
  canBotCollect,
  canBotDeliver,
  canBotBuildDrill,
  canBotPickup,
  canBotClaim,
} from './botModeAI';
import { getPlayerCarrying } from '../core/carryables';
import { getModeRules } from '../core/modeRules';
import { getBotInteractionInProgress } from './botModeAI';
import { findTransitPath } from './transitGraph';
import type { SeededRandom } from '../core/random';

// =============================================================================
// BUILD SITUATION
// =============================================================================

/**
 * Build a snapshot of the bot's situation for graph evaluation.
 * All the expensive lookups happen here once; graphs just read flags.
 */
export function buildBotSituation(
  player: PlayerState,
  gameState: GameState,
  config: BotConfig,
  fogContext?: BotFogContext,
  transitGraph?: TransitGraph,
): BotSituation {
  const mode = gameState.config.modeConfig.mode;
  const modeConfig = gameState.config.modeConfig;
  const rules = getModeRules(modeConfig);

  // Location
  const homePlanet = modeConfig.homeTeamPlanets?.[player.teamIndex];
  const onHomePlanet = homePlanet !== undefined && player.planetIndex === homePlanet;

  // Objective target (cross-planet)
  const objTarget = getBotObjectiveTarget(player, gameState);
  const onObjectivePlanet = objTarget !== null && objTarget.planetIndex === player.planetIndex;
  const objectiveOnOtherPlanet = objTarget !== null && objTarget.planetIndex !== player.planetIndex;

  // Same-planet objective (within walking distance)
  const samePlanetObj = getBotSamePlanetObjectiveDir(player, gameState);
  const objectiveOnThisPlanet = samePlanetObj !== null;

  // Near objective: check canBot* helpers for immediate actions
  const canPickupNow = canBotPickup(player, gameState);
  const canCollectNow = canBotCollect(player, gameState);
  const canInteractNow = canBotInteract(player, gameState);
  const canDeliverNow = canBotDeliver(player, gameState);
  const nearObjectiveOnPlanet = !!(canPickupNow || canCollectNow || canInteractNow || canDeliverNow);

  // Carrying
  const carrying = getPlayerCarrying(player.id, gameState.carryables);
  const carryingObjective = carrying !== null && carrying.teamIndex !== player.teamIndex;
  // S6 / §7.5: the seat this unit could take this turn — an empty, standing emplacement on its planet within walk + reach
  const mounted = !!player.mount;
  let seatTarget: BotSituation['seatTarget'];
  if (!mounted && tuningFor(config).mounting.takeSeats && !player.airborne) {
    const planet = gameState.planets[player.planetIndex];
    let best = Infinity;
    for (const st of gameState.structures) {
      if (!st.seats?.[0] || st.seats[0].occupant || st.health <= 0 || st.planetIndex !== player.planetIndex || !planet) continue;
      const d = arcDistance(player.surfaceDir, st.surfaceDir, planet.radius);
      // walk to the edge of reach and keep enough movement to mount and fire this turn
      if (Math.max(0, d - INTERACT_RANGE * 0.9) > player.movementRemaining - MOUNT_MOVEMENT_COST - FIRE_MOVEMENT_COST || d >= best) continue;
      best = d;
      seatTarget = { structureId: st.id, surfaceDir: st.surfaceDir, inReach: d <= INTERACT_RANGE };
    }
  }

  // Reachability — check both direct and multi-hop routes.
  // Even if transit graph says unreachable, the comprehensive sweep in computeBotJump
  // may still find a route, so this is advisory (used for scoring), not a hard gate.
  let objectivePlanetReachable = false;
  if (objTarget && objTarget.planetIndex !== player.planetIndex && transitGraph) {
    const entries = transitGraph.get(player.planetIndex);
    if (entries?.some(e => e.targetPlanetIndex === objTarget.planetIndex)) {
      objectivePlanetReachable = true;
    } else {
      // Check multi-hop: even if not directly reachable, BFS might find a path
      const fuelBonus = player.jumppackFuel > 0 ? JUMPPACK_VELOCITY_BONUS : 0;
      const path = findTransitPath(transitGraph, player.planetIndex, objTarget.planetIndex, gameState.planets, player.movementRemaining, fuelBonus);
      objectivePlanetReachable = path !== null && path.length >= 2;
    }
  }

  // Capability
  const tethered = !!player.tetheredTurns && player.tetheredTurns > 0;
  const canFire = hasAnyAmmo(player) && player.movementRemaining >= FIRE_MOVEMENT_COST && !player.holdFire;
  const canJump = player.movementRemaining >= JUMP_MIN_COST && !player.airborne && !tethered;
  const canMove = player.movementRemaining >= 0.5 && !player.airborne && !tethered;

  // Conquest: does the team already hold this planet?
  const teamStakeOnThisPlanet = gameState.plantedFlags.some(
    f => f.teamIndex === player.teamIndex && f.planetIndex === player.planetIndex && f.alive !== false,
  );

  // An interaction already under way here: the bot must hold position for it
  const holdingInteraction = getBotInteractionInProgress(player, gameState) !== null;

  // Mode action detection. Stake and drill actions only count under their own
  // objective: every unit carries a flag, so an unguarded plantStake check used
  // to shadow buildDrill (excavation bots never built a drill).
  const stakes = rules.objective === 'stakes';
  let canDoModeAction = false;
  let modeActionType: BotPlanStep['type'] | null = null;
  if (canPickupNow) { canDoModeAction = true; modeActionType = 'pickup'; }
  else if (canDeliverNow) { canDoModeAction = true; modeActionType = 'deliver'; }
  else if (canCollectNow) { canDoModeAction = true; modeActionType = 'collect'; }
  else if (canInteractNow) { canDoModeAction = true; modeActionType = 'interact'; }
  else if (rules.objective === 'excavation' && canBotBuildDrill(player, gameState)) { canDoModeAction = true; modeActionType = 'buildDrill'; }
  else if (stakes && canBotPlantStake(player, gameState)) { canDoModeAction = true; modeActionType = 'plantStake'; }
  else if (stakes && canBotRemoveStake(player, gameState)) { canDoModeAction = true; modeActionType = 'removeStake'; }
  else if (canBotClaim(player, gameState)) { canDoModeAction = true; modeActionType = 'claim'; }

  // Context
  const lowHealth = player.health <= 30;
  const spotted = fogContext ? fogContext.visibleEnemyIds.length > 0 : false;
  const noVisibleEnemies = fogContext ? fogContext.visibleEnemyIds.length === 0 : false;
  const hasProbes = (player.loadout.ammo.probe === null || (player.loadout.ammo.probe ?? 0) > 0);
  const hasFlags = player.flagsRemaining > 0;

  // Environment mode fields
  let darkenedPlanets: number[] | undefined;
  let threatenedPlanets: number[] | undefined;
  let turnsUntilDoom: number | undefined;

  const ms = gameState.modeState;
  if (ms.blackout) {
    darkenedPlanets = ms.blackout.darkenedPlanets;
    // Check if current planet is next to darken
    const nextToDarken = ms.blackout.schedule.slice(0, gameState.config.modeConfig.blackoutRate ?? 1);
    threatenedPlanets = nextToDarken;
    if (nextToDarken.includes(player.planetIndex)) {
      turnsUntilDoom = ms.blackout.nextBlackoutTurn - gameState.turnNumber;
    }
  }
  if (ms.lastLight && ms.lastLight.schedule.length > 0) {
    const next = ms.lastLight.schedule[0];
    threatenedPlanets = [...(threatenedPlanets ?? []), next];
    if (next === player.planetIndex) {
      const doom = ms.lastLight.nextDestroyTurn - gameState.turnNumber;
      turnsUntilDoom = turnsUntilDoom === undefined ? doom : Math.min(turnsUntilDoom, doom);
    }
  }

  // Alert layer (Phase 57)
  let alert: BotSituation['alert'];
  if (ms.alert) {
    const level = alertLevelAt(gameState, player.planetIndex);
    const guard = isGuard(gameState, player);
    const lastSeen = ms.alert.byPlanet[player.planetIndex]?.lastSeen;
    let reinforceTarget: number | undefined;
    if (guard && level !== 'alert' && transitGraph) {
      for (const [k, p] of Object.entries(ms.alert.byPlanet)) {
        const i = Number(k);
        if (p.level !== 'alert' || i === player.planetIndex || !gameState.planets[i]?.alive) continue;
        const direct = transitGraph.get(player.planetIndex)?.some(e => e.targetPlanetIndex === i);
        if (direct) { reinforceTarget = i; break; }
      }
    }
    const spottedNow = fogContext ? fogContext.visibleEnemyIds.length > 0 : false;
    const recentSighting = lastSeen !== undefined && gameState.turnNumber - lastSeen.turn <= 2;
    const ownLoose = gameState.carryables.some(c => c.alive && c.teamIndex === player.teamIndex
      && c.sourcePlanetIndex === player.planetIndex && (c.carriedBy !== null || c.planetIndex !== player.planetIndex));
    const intruder = spottedNow || recentSighting || ownLoose;
    alert = {
      isGuard: guard, level, reinforceTarget, intruder,
      ...(lastSeen ? { lastSeenDir: lastSeen.surfaceDir, lastSeenTurn: lastSeen.turn } : {}),
      ...(objTarget ? { objectiveLevel: alertLevelAt(gameState, objTarget.planetIndex) } : {}),
    };
  }

  // Objective scores
  let teamScore = 0;
  let enemyScore = 0;
  if (ms.ctf) {
    const scores = ms.ctf.scores;
    teamScore = scores[player.teamIndex] ?? 0;
    for (const [key, val] of Object.entries(scores)) {
      if (Number(key) !== player.teamIndex && val > enemyScore) {
        enemyScore = val;
      }
    }
  }

  return {
    mode,
    objective: rules.objective,
    environments: rules.environments,
    onHomePlanet,
    onObjectivePlanet,
    nearObjectiveOnPlanet,
    holdingInteraction,
    carryingObjective,
    ...(mounted ? { mounted: true } : {}),
    ...(seatTarget ? { seatTarget } : {}),
    objectiveOnThisPlanet,
    objectiveOnOtherPlanet,
    objectivePlanetReachable,
    teamStakeOnThisPlanet,
    canFire,
    canJump,
    canMove,
    canDoModeAction,
    modeActionType,
    lowHealth,
    spotted,
    noVisibleEnemies,
    hasProbes,
    hasFlags,
    teamScore,
    enemyScore,
    darkenedPlanets,
    threatenedPlanets,
    turnsUntilDoom,
    ...(alert ? { alert } : {}),
  };
}

// =============================================================================
// PLAN DISPATCHER
// =============================================================================

/**
 * Build a turn plan for a bot based on mode and situation.
 * Every mode returns a plan; the runner executes steps without knowing the mode.
 */
export function planBotTurn(
  player: PlayerState,
  gameState: GameState,
  sit: BotSituation,
  config: BotConfig,
  rng: SeededRandom,
  transitGraph?: TransitGraph,
  fogContext?: BotFogContext,
): BotTurnSteps {
  const { personality, skill } = config;

  // S6 / WORKBOARD §7.5 — mount as a state. A seated unit fires from the seat and does nothing else (it cannot walk or
  // jump; dismounting is not in the bot's repertoire yet). A unit with an empty seat in reach this turn walks to it,
  // takes it, and fires from it — the turn brain drives whatever it sits in.
  if (sit.mounted) return { steps: sit.canFire ? [{ type: 'fire' }] : [], debugSource: 'seat:fire' };
  if (sit.seatTarget) {
    const steps: BotPlanStep[] = [];
    if (!sit.seatTarget.inReach) steps.push({ type: 'move', toward: 'seat' });
    steps.push({ type: 'mount', structureId: sit.seatTarget.structureId });
    if (sit.canFire) steps.push({ type: 'fire', fresh: true });
    return { steps, debugSource: 'seat:take' };
  }

  // Phase 57: a guard patrols, investigates, reinforces — or, once its planet is alert with an intruder on it, holds the
  // post (57.11) — instead of taking the ordinary plan. An alert planet with no intruder left on it is the ordinary plan:
  // the garrison's counter-raid (the twelfth fill: holding a cleared planet only cost the garrison its captures)
  if (sit.alert?.isGuard && (sit.alert.level !== 'alert' || sit.alert.intruder)) return planGuardTurn(sit);
  const sneaking = sneakingNow(gameState, player, config);

  // Plain deathmatch: flat scoring picks the action (commands are weighed
  // inside it), then the action becomes the same step list the other plans produce.
  if (sit.objective === 'none' && sit.environments.length === 0) {
    const action = chooseBotAction(player, gameState, config, rng, transitGraph, fogContext);
    const dm = deathmatchPlanFor(action);
    return sneaking ? sneakFilter(dm, sit) : dm;
  }

  // Command overrides first
  if (player.pendingCommand) {
    const cmdPlan = planFromCommand(player.pendingCommand, sit, personality);
    if (cmdPlan) return cmdPlan;
  }

  // The objective picks the plan. With no objective, the environment does:
  // blackout and last-light plans are survival plans around the schedule.
  // (An objective stacked on an environment still plans for the objective;
  // the situation carries threatenedPlanets / turnsUntilDoom for it.)
  let plan: BotTurnSteps;
  switch (sit.objective) {
    case 'ctf':
      plan = planCtfTurn(sit, personality, skill, rng);
      break;
    case 'stakes':
      plan = planDominionTurn(sit, personality, skill, rng);
      break;
    case 'signalTrace':
      plan = planSignalTraceTurn(sit, personality, skill, rng);
      break;
    case 'uplink':
      plan = planUplinkTurn(sit, personality, skill, rng);
      break;
    case 'salvage':
      plan = planSalvageRunTurn(sit, personality, skill, rng);
      break;
    case 'excavation':
      plan = planExcavationTurn(sit, personality, skill, rng);
      break;
    case 'points':   // 59.6: capture points run on the action clock; a turn bot just fights
    case 'none':
      if (sit.environments.includes('blackout')) plan = planBlackoutTurn(sit, personality, skill, rng);
      else if (sit.environments.includes('lastLight')) plan = planLastLightTurn(sit, personality, skill, rng);
      else plan = planCombatFallback(sit, personality, rng);
      break;
  }

  // Low-skill bots get simplified plans
  if (skill < 0.3) {
    plan = simplifyPlan(plan);
  }

  // Phase 57: a sneaking infiltrator fires no projectile while its planet and the objective planet are quiet
  if (sneaking) plan = sneakFilter(plan, sit);

  return plan;
}

// =============================================================================
// GUARDS AND SNEAKING (Phase 57)
// =============================================================================

/**
 * A guard on a planet that is not alert: reinforce an alert planet it can jump
 * to; on a suspicious planet walk toward where the enemy was last seen and
 * shoot if one is in sight; otherwise patrol. It never fires at nothing.
 */
export function planGuardTurn(sit: BotSituation): BotTurnSteps {
  const a = sit.alert!;
  const steps: BotPlanStep[] = [];
  // 57.11 — the garrison holds: on an alert planet a guard shoots what it sees, hunts the sighting, retrieves or stands
  // on its objective, and never jumps away (the eleventh fill: alert guards took the ordinary CTF plan and advanced on the
  // enemy core while the carrier walked home — sensors saw the infiltrator and it did not matter)
  if (a.level === 'alert') {
    if (sit.spotted && sit.canFire) steps.push({ type: 'fire', fresh: true });
    if (sit.canDoModeAction && sit.modeActionType) steps.push({ type: sit.modeActionType } as BotPlanStep);
    else if (sit.canMove && !sit.holdingInteraction) {
      if (a.lastSeenDir) steps.push({ type: 'move', toward: 'investigate' });
      else if (sit.objectiveOnThisPlanet) steps.push({ type: 'move', toward: 'objective' });
      else steps.push({ type: 'move', toward: 'patrol' });
    }
    return { steps, debugSource: steps.length ? 'guard:holdPost' : 'guard:hold' };
  }
  if (a.reinforceTarget !== undefined && sit.canJump) {
    steps.push({ type: 'jump', toward: 'reinforce' });
    return { steps, debugSource: 'guard:reinforce' };
  }
  if (a.level === 'suspicious') {
    if (a.lastSeenDir && sit.canMove) steps.push({ type: 'move', toward: 'investigate' });
    if (sit.spotted && sit.canFire) steps.push({ type: 'fire', fresh: true });
    if (steps.length) return { steps, debugSource: 'guard:investigate' };
  }
  // 57.7b: a quiet garrison works the objective on its own planet — collect, deliver, hold the post, plant the
  // stake — so it is a side in the mode, not furniture (it never leaves the planet for it; reinforcing is the only jump)
  if (sit.canDoModeAction && sit.modeActionType) {
    steps.push({ type: sit.modeActionType } as BotPlanStep);
    return { steps, debugSource: `guard:${sit.modeActionType}` };
  }
  if (sit.objectiveOnThisPlanet && sit.canMove && !sit.holdingInteraction) {
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'guard:work' };
  }
  if (sit.canMove && !sit.holdingInteraction) steps.push({ type: 'move', toward: 'patrol' });
  return { steps, debugSource: steps.length ? 'guard:patrol' : 'guard:hold' };
}

/** Strip projectile shots (not probes — reconnaissance is quiet) from a plan while the infiltrator's planet and its objective planet are below alert. */
export function sneakFilter(plan: BotTurnSteps, sit: BotSituation): BotTurnSteps {
  const a = sit.alert!;
  const loudAlready = a.level === 'alert' || a.objectiveLevel === 'alert';
  if (loudAlready) return plan;
  const steps = plan.steps.filter(s => s.type !== 'fire');
  if (steps.length === 0 && sit.canMove) steps.push({ type: 'move', toward: sit.objectiveOnThisPlanet ? 'objective' : 'combat' });
  // 57.10 — after a pickup, leave: the sneaking walk takes the carrier to the least exposed spot this turn,
  // and next turn's plan jumps it home
  if (steps.some(s => s.type === 'pickup') && !steps.some(s => s.type === 'move')) steps.push({ type: 'move', toward: 'objective' });
  return { steps, debugSource: `${plan.debugSource}+sneak` };
}

// =============================================================================
// DEATHMATCH — flat-scored action as a step list
// =============================================================================

/**
 * The deathmatch turn as steps. Each list reproduces the turn flow the runner
 * used to hard-code for that action:
 *   fire        shoot, then the exposure-aware post-fire phase
 *   move/jump   reposition, then shoot from the new spot if still able, then post-fire
 *   build/heal  do it (or end the turn if it cannot be done), pause, then shoot
 *   shop        order a delivery (the drone ends the turn) — or end the turn
 *   skip        yield
 *   other       mode action, pause, then shoot
 */
export function deathmatchPlanFor(action: BotAction): BotTurnSteps {
  const thenFire: BotPlanStep[] = [{ type: 'fire', fresh: true }, { type: 'postFire' }];
  switch (action) {
    case 'fire':
      return { steps: [{ type: 'fire', yieldIfNoShot: true }, { type: 'postFire' }], debugSource: 'dm:fire' };
    case 'move':
      return { steps: [{ type: 'move', toward: 'combat', required: true }, ...thenFire], debugSource: 'dm:move' };
    case 'jump':
      return { steps: [{ type: 'jump', toward: 'combat' }, ...thenFire], debugSource: 'dm:jump' };
    case 'build':
      return { steps: [{ type: 'build', required: true, pauseMs: 500 }, ...thenFire], debugSource: 'dm:build' };
    case 'heal':
      return { steps: [{ type: 'heal', required: true, pauseMs: 300 }, ...thenFire], debugSource: 'dm:heal' };
    case 'shop':
      return { steps: [{ type: 'shop', required: true }], debugSource: 'dm:shop' };
    case 'skip':
      return { steps: [{ type: 'yield' }], debugSource: 'dm:skip' };
    default:
      return { steps: [{ type: action, pauseMs: 300 }, ...thenFire], debugSource: `dm:${action}` };
  }
}

// =============================================================================
// COMMAND OVERRIDE
// =============================================================================

function planFromCommand(
  cmd: BotCommand,
  sit: BotSituation,
  personality: BotPersonality,
): BotTurnSteps | null {
  const steps: BotPlanStep[] = [];

  switch (cmd.type) {
    case 'fireAtTarget':
    case 'fireAtLocation':
      if (sit.canFire) steps.push({ type: 'fire' });
      return steps.length > 0 ? { steps, debugSource: 'cmd:fire' } : null;

    case 'fireProbe':
      if (sit.canFire) steps.push({ type: 'fireProbe' });
      return steps.length > 0 ? { steps, debugSource: 'cmd:probe' } : null;

    case 'moveToPlanet':
      if (sit.canFire && personality.aggression > 0.2) steps.push({ type: 'fire' });
      if (sit.canJump) steps.push({ type: 'jump', toward: 'objective' });
      return steps.length > 0 ? { steps, debugSource: 'cmd:moveToPlanet' } : null;

    case 'holdPosition':
      if (cmd.holdPreset === 'advance' || cmd.holdPreset === 'waypoint') {
        if (sit.canFire) steps.push({ type: 'fire' });
        if (sit.canMove) steps.push({ type: 'move', toward: 'holdDir' });
      } else {
        if (sit.canMove) steps.push({ type: 'move', toward: 'holdDir' });
        if (sit.canFire) steps.push({ type: 'fire' });
      }
      return steps.length > 0 ? { steps, debugSource: 'cmd:hold' } : null;

    case 'prioritizeEnemy':
      // Soft influence — doesn't override plan structure, just affects target selection
      return null;
  }
}

// =============================================================================
// CTF GRAPH
// =============================================================================

function planCtfTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];
  const fireFirst = sit.canFire && personality.aggression > 0.2;

  // === CARRYING ENEMY CORE ===
  if (sit.carryingObjective) {
    if (sit.onHomePlanet) {
      // Walk to capture zone (auto-capture triggers on walk)
      if (fireFirst) steps.push({ type: 'fire' });
      steps.push({ type: 'move', toward: 'objective' });
      return { steps, debugSource: 'ctf:carry-home' };
    }
    // Need to get home — fire then jump (or walk toward jump side)
    if (fireFirst) steps.push({ type: 'fire' });
    if (sit.canJump) steps.push({ type: 'jump', toward: 'objective' });
    steps.push({ type: 'move', toward: 'objective' }); // fallback walk
    return { steps, debugSource: 'ctf:carry-return' };
  }

  // === CORE ON THIS PLANET ===
  if (sit.objectiveOnThisPlanet) {
    if (sit.nearObjectiveOnPlanet && sit.canDoModeAction) {
      if (fireFirst) steps.push({ type: 'fire' });
      steps.push({ type: 'pickup' });
      steps.push({ type: 'postFire' });
      return { steps, debugSource: 'ctf:pickup' };
    }
    // Walk to core
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'ctf:approach-core' };
  }

  // === CORE ON OTHER PLANET ===
  if (sit.objectiveOnOtherPlanet) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) {
      steps.push({ type: 'jump', toward: 'objective' });
    }
    steps.push({ type: 'move', toward: 'objective' }); // walk toward jump side
    return { steps, debugSource: 'ctf:advance' };
  }

  // === FALLBACK: combat ===
  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// DOMINION / SOVEREIGNTY GRAPH
// =============================================================================

function planDominionTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];
  const fireFirst = sit.canFire && personality.aggression > 0.2;

  // === CAN PLANT STAKE ON THIS PLANET ===
  // Spread before stacking: with a live stake already here and an unstaked planet
  // reachable, carry the next flag there instead of doubling up at home. Stacking
  // is still right when there is nowhere else to go.
  const spreadInstead = sit.teamStakeOnThisPlanet && sit.objectiveOnOtherPlanet
    && sit.canJump && sit.objectivePlanetReachable;
  if (sit.canDoModeAction && sit.modeActionType === 'plantStake' && !spreadInstead) {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'plantStake' });
    steps.push({ type: 'postFire' });
    return { steps, debugSource: 'dom:plant' };
  }

  // === CAN REMOVE ENEMY STAKE ON THIS PLANET ===
  if (sit.canDoModeAction && sit.modeActionType === 'removeStake') {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'removeStake' });
    steps.push({ type: 'postFire' });
    return { steps, debugSource: 'dom:remove' };
  }

  // === OBJECTIVE ON OTHER PLANET (unstaked planet) ===
  if (sit.objectiveOnOtherPlanet && sit.hasFlags) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) {
      steps.push({ type: 'jump', toward: 'objective' });
    }
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'dom:advance' };
  }

  // === FALLBACK: combat ===
  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// SIGNAL TRACE GRAPH
// =============================================================================

function planSignalTraceTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];
  const fireFirst = sit.canFire && personality.aggression > 0.2;

  // === WORKING A POST: HOLD POSITION ===
  // The interaction ticks at the round wrap and is cancelled if the bot leaves
  // range, so fire from where it stands and do not walk.
  if (sit.holdingInteraction) {
    if (sit.canFire) steps.push({ type: 'fire' });
    steps.push({ type: 'skip' });
    return { steps, debugSource: 'sig:hold-post' };
  }

  // === CAN INTERACT WITH LISTENING POST ON THIS PLANET (in range) ===
  if (sit.canDoModeAction && sit.modeActionType === 'interact') {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'interact' });
    return { steps, debugSource: 'sig:interact' };
  }

  // === OBJECTIVE ON THIS PLANET (walk to post) ===
  if (sit.objectiveOnThisPlanet || sit.onObjectivePlanet) {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'sig:approach-post' };
  }

  // === OBJECTIVE ON OTHER PLANET (unactivated post or signal) ===
  if (sit.objectiveOnOtherPlanet) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) {
      steps.push({ type: 'jump', toward: 'objective' });
    }
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'sig:advance' };
  }

  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// UPLINK GRAPH
// =============================================================================

function planUplinkTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];
  const fireFirst = sit.canFire && personality.aggression > 0.2;

  // === CARRYING FRAGMENT: DELIVER ===
  if (sit.carryingObjective) {
    if (sit.canDoModeAction && sit.modeActionType === 'deliver') {
      // On antenna planet, can deliver now
      if (fireFirst) steps.push({ type: 'fire' });
      steps.push({ type: 'deliver' });
      steps.push({ type: 'postFire' });
      return { steps, debugSource: 'uplink:deliver' };
    }
    // Need to get to antenna planet
    if (fireFirst) steps.push({ type: 'fire' });
    if (sit.canJump) steps.push({ type: 'jump', toward: 'objective' });
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'uplink:carry-to-antenna' };
  }

  // === COLLECTIBLE ON THIS PLANET ===
  if (sit.canDoModeAction && sit.modeActionType === 'collect') {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'collect' });
    steps.push({ type: 'postFire' });
    return { steps, debugSource: 'uplink:collect' };
  }

  // === FRAGMENT ON OTHER PLANET ===
  if (sit.objectiveOnOtherPlanet) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) {
      steps.push({ type: 'jump', toward: 'objective' });
    }
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'uplink:advance' };
  }

  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// SALVAGE RUN GRAPH
// =============================================================================

function planSalvageRunTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];
  const fireFirst = sit.canFire && personality.aggression > 0.2;

  // === CARRYING SALVAGE: DELIVER TO DEPOT ===
  if (sit.carryingObjective) {
    if (sit.canDoModeAction && sit.modeActionType === 'deliver') {
      if (fireFirst) steps.push({ type: 'fire' });
      steps.push({ type: 'deliver' });
      steps.push({ type: 'postFire' });
      return { steps, debugSource: 'salvage:deliver' };
    }
    // Need to get to depot planet
    if (fireFirst) steps.push({ type: 'fire' });
    if (sit.canJump) steps.push({ type: 'jump', toward: 'objective' });
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'salvage:carry-to-depot' };
  }

  // === COLLECTIBLE ON THIS PLANET ===
  if (sit.canDoModeAction && sit.modeActionType === 'collect') {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'collect' });
    steps.push({ type: 'postFire' });
    return { steps, debugSource: 'salvage:collect' };
  }

  // === SALVAGE ON OTHER PLANET ===
  if (sit.objectiveOnOtherPlanet) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) {
      steps.push({ type: 'jump', toward: 'objective' });
    }
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'salvage:advance' };
  }

  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// EXCAVATION GRAPH
// =============================================================================

function planExcavationTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];
  const fireFirst = sit.canFire && personality.aggression > 0.2;

  // === CAN PICK UP ARTIFACT ===
  if (sit.canDoModeAction && sit.modeActionType === 'pickup') {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'pickup' });
    steps.push({ type: 'postFire' });
    return { steps, debugSource: 'exc:pickup' };
  }

  // === CAN BUILD DRILL ===
  if (sit.canDoModeAction && sit.modeActionType === 'buildDrill') {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'buildDrill' });
    return { steps, debugSource: 'exc:build-drill' };
  }

  // === OBJECTIVE ON THIS PLANET (walk to artifact/drill site) ===
  if (sit.objectiveOnThisPlanet || sit.onObjectivePlanet) {
    if (fireFirst) steps.push({ type: 'fire' });
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'exc:approach' };
  }

  // === OBJECTIVE ON OTHER PLANET ===
  if (sit.objectiveOnOtherPlanet) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) {
      steps.push({ type: 'jump', toward: 'objective' });
    }
    steps.push({ type: 'move', toward: 'objective' });
    return { steps, debugSource: 'exc:advance' };
  }

  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// BLACKOUT GRAPH
// =============================================================================

function planBlackoutTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];

  // On a soon-to-darken planet: escape first
  if (sit.turnsUntilDoom !== undefined && sit.turnsUntilDoom <= 2) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) steps.push({ type: 'jump', toward: 'escape' });
    return { steps, debugSource: 'blackout:flee-dark' };
  }

  // On a dark planet: move cautiously, fire if enemies visible
  if (sit.darkenedPlanets?.includes(sit.onHomePlanet ? 0 : -1)) {
    // This won't trigger from the situation flag; handled below
  }

  // Standard combat with environmental awareness
  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// LAST LIGHT GRAPH
// =============================================================================

function planLastLightTurn(
  sit: BotSituation,
  personality: BotPersonality,
  _skill: number,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];

  // On doomed planet: PRIORITY ESCAPE
  if (sit.turnsUntilDoom !== undefined && sit.turnsUntilDoom <= 2) {
    if (sit.canFire) steps.push({ type: 'fire' });
    if (sit.canJump) {
      steps.push({ type: 'jump', toward: 'escape' });
    }
    if (steps.length === 0) steps.push({ type: 'skip' });
    return { steps, debugSource: 'lastlight:flee-doom' };
  }

  // Standard combat
  return planCombatFallback(sit, personality, _rng);
}

// =============================================================================
// COMBAT FALLBACK
// =============================================================================

/**
 * When no mode objective is actionable, fall back to combat.
 * Fire, then reposition.
 */
function planCombatFallback(
  sit: BotSituation,
  personality: BotPersonality,
  _rng: SeededRandom,
): BotTurnSteps {
  const steps: BotPlanStep[] = [];

  // Scout probe when blind
  if (sit.noVisibleEnemies && sit.hasProbes && sit.canFire) {
    steps.push({ type: 'fireProbe' });
    if (sit.canMove) steps.push({ type: 'move', toward: 'combat' });
    return { steps, debugSource: 'combat:probe' };
  }

  if (sit.canFire) steps.push({ type: 'fire' });

  // Low health or spotted: escape
  if (sit.lowHealth && sit.canJump) {
    steps.push({ type: 'jump', toward: 'escape' });
  } else if (sit.spotted && sit.canMove) {
    steps.push({ type: 'move', toward: 'antipodal' });
  } else if (sit.canMove && personality.mobility > 0.4) {
    steps.push({ type: 'move', toward: 'combat' });
  }

  if (steps.length === 0) {
    steps.push({ type: 'skip' });
  }

  return { steps, debugSource: 'combat:fallback' };
}

// =============================================================================
// PLAN SIMPLIFICATION
// =============================================================================

/**
 * Simplify plans for low-skill bots (skill < 0.3).
 * They just fire and skip — no complex multi-step plans.
 */
function simplifyPlan(plan: BotTurnSteps): BotTurnSteps {
  const simplified: BotPlanStep[] = [];
  let hasFire = false;

  for (const step of plan.steps) {
    if ((step.type === 'fire' || step.type === 'fireProbe') && !hasFire) {
      simplified.push(step);
      hasFire = true;
    }
    // Keep immediate on-planet actions (pickup, collect, deliver, plantStake)
    if (['pickup', 'collect', 'deliver', 'plantStake', 'interact'].includes(step.type)) {
      simplified.push(step);
    }
    // Guards keep their movement (Phase 57): a patrol with no walk is a statue
    if ((step.type === 'move' && (step.toward === 'patrol' || step.toward === 'investigate')) || (step.type === 'jump' && step.toward === 'reinforce')) {
      simplified.push(step);
    }
  }

  if (simplified.length === 0) {
    simplified.push({ type: 'skip' });
  }

  return { steps: simplified, debugSource: plan.debugSource + ':simplified' };
}
